import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { 
  Calendar, 
  Clock, 
  MapPin, 
  Users, 
  Building2, 
  BookOpen, 
  CheckCircle, 
  AlertCircle,
  ArrowLeft,
  CreditCard,
  User,
  Phone,
  Mail,
  Check,
  X
} from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import traineeService, { TrainingWithSessions, Session } from '../../services/trainee.service';
import i18n from '../../i18n';

const TrainingSubscriptionPage: React.FC = () => {
  const { t } = useTranslation();
  const { trainingId } = useParams<{ trainingId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Map format values to translations
  const getFormatTranslation = (format: string) => {
    const formatMap: Record<string, string> = {
      'En présentiel': t('formations.format.presentiel'),
      'En ligne': t('formations.format.online'),
      'Hybride': t('formations.format.hybrid'),
      'Présentiel': t('formations.format.presentiel'),
      'À distance': t('formations.format.online')
    };
    return formatMap[format] || format;
  };

  // Map duration values to translations
  const getDurationTranslation = (duration: string) => {
    if (!duration) return duration;
    // Replace "jours" with "days" in English, keep "jours" in French
    if (i18n.language === 'en') {
      return duration.replace(/jours/g, 'days').replace(/jour/g, 'day');
    }
    return duration;
  };
  const [training, setTraining] = useState<TrainingWithSessions | null>(null);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingSubscription, setExistingSubscription] = useState<any>(null);
  const [showChangeDialog, setShowChangeDialog] = useState(false);
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [formData, setFormData] = useState({
    current_position: '',
    company_name: '',
    department: '',
    years_of_experience: '',
    has_special_accommodation: false,
    special_accommodation_details: ''
  });

  const isSessionInPast = (sessionDate: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today
    const sessionDateObj = new Date(sessionDate);
    return sessionDateObj < today;
  };

  useEffect(() => {
    if (trainingId) {
      loadTrainingSessions();
    }
    
    // Get form data from navigation state or from database
    if (location.state?.formData) {
      setFormData(location.state.formData);
    } else {
      // Load subscription info from database
      loadSubscriptionInfo();
    }
  }, [trainingId, location.state]);

  const loadSubscriptionInfo = async () => {
    try {
      const subscriptionInfo = await traineeService.getSubscriptionInfo();
      
      const formDataToSet = {
        current_position: subscriptionInfo.current_position || '',
        company_name: subscriptionInfo.company_name || '',
        department: subscriptionInfo.department || '',
        years_of_experience: subscriptionInfo.years_of_experience || '',
        has_special_accommodation: subscriptionInfo.has_special_accommodation || false,
        special_accommodation_details: subscriptionInfo.special_accommodation_details || ''
      };
      
      setFormData(formDataToSet);
    } catch (error) {
    }
  };

  const loadTrainingSessions = async () => {
    try {
      setLoading(true);
      const data = await traineeService.getTrainingSessions(parseInt(trainingId!));
      setTraining(data);
      
      // Auto-select first available session (not in the past)
      const availableSession = data.sessions.find(s => s.availableSpots > 0 && !isSessionInPast(s.sessionDate));
      if (availableSession) {
        setSelectedSession(availableSession);
      }
    } catch (error) {
      setError(t('trainee.trainingSubscription.errors.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    if (!selectedSession) return;

    try {
      setSubscribing(true);
      setError(null);
      
      const result = await traineeService.subscribeToSession(selectedSession.id, formData);
      
      // Check if user is already subscribed to another session
      if (result.error === 'ALREADY_SUBSCRIBED') {
        setExistingSubscription(result.existingSubscription);
        setShowChangeDialog(true);
        return;
      }
      
      setShowSuccess(true);
      
      // Redirect to my trainings after 3 seconds
      setTimeout(() => {
        navigate('/my-trainings');
      }, 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || t('trainee.trainingSubscription.errors.subscribeError'));
    } finally {
      setSubscribing(false);
    }
  };

  const handleSaveInfo = async () => {
    try {
      // Here you could save the info to a user profile or temporary storage
      // For now, we'll just update the local state
      setIsEditingInfo(false);
    } catch (error) {
    }
  };

  const handleCancelEdit = () => {
    setIsEditingInfo(false);
    // Reload original data if needed
    loadSubscriptionInfo();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleChangeSession = async () => {
    if (!selectedSession) return;

    try {
      setSubscribing(true);
      setError(null);
      
      const result = await traineeService.changeSessionSubscription(selectedSession.id);
      
      setShowChangeDialog(false);
      setShowSuccess(true);
      
      // Redirect to my trainings after 3 seconds
      setTimeout(() => {
        navigate('/my-trainings');
      }, 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || t('trainee.trainingSubscription.errors.changeError'));
    } finally {
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title={t('trainee.trainingSubscription.title')}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!training) {
    return (
      <DashboardLayout title={t('trainee.trainingSubscription.trainingNotFound')}>
        <div className="text-center py-16">
          <p className="text-gray-600">{t('trainee.trainingSubscription.trainingNotFound')}</p>
        </div>
      </DashboardLayout>
    );
  }

  if (showSuccess) {
    return (
      <DashboardLayout title={t('trainee.trainingSubscription.success.title')}>
        <div className="max-w-2xl mx-auto">
          <div className="bg-green-50 border border-green-200 rounded-3xl p-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-green-800 mb-2">
              {t('trainee.trainingSubscription.success.title')}
            </h2>
            <p className="text-green-600 mb-6">
              {t('trainee.trainingSubscription.success.subtitle', { title: training.title })}
            </p>
            <div className="bg-white rounded-2xl p-6 mb-6">
              <h3 className="font-semibold text-gray-900 mb-4">{t('trainee.trainingSubscription.success.details.title')}</h3>
              <div className="space-y-3 text-left">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('trainee.trainingSubscription.success.details.training')}</span>
                  <span className="font-medium">{training.title}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('trainee.trainingSubscription.success.details.date')}</span>
                  <span className="font-medium">{selectedSession && formatDate(selectedSession.sessionDate)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('trainee.trainingSubscription.success.details.center')}</span>
                  <span className="font-medium">{training.center.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('trainee.trainingSubscription.success.details.price')}</span>
                  <span className="font-medium">{selectedSession && formatCurrency(selectedSession.price)}</span>
                </div>
              </div>
            </div>
            <p className="text-sm text-green-600">
              {t('trainee.trainingSubscription.success.redirectMessage')}
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={t('trainee.trainingSubscription.title')}>
      <div className="max-w-6xl mx-auto">
        {/* Modern Header */}
        <div className="relative overflow-hidden bg-gradient-to-br from-brand-primary via-brand-primary/90 to-purple-600 rounded-2xl shadow-xl mb-6">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative p-6 md:p-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate(-1)}
                  className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl hover:bg-white/30 transition-all duration-200 group"
                >
                  <ArrowLeft className="h-5 w-5 text-white group-hover:scale-110 transition-transform" />
                </button>
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                    {training.title}
                  </h1>
                  <p className="text-white/90 text-lg">
                    {training.center.name}
                  </p>
                </div>
              </div>
              <div className="hidden md:block">
                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4">
                  <div className="flex items-center gap-2 text-white">
                    <MapPin className="h-5 w-5" />
                    <span className="text-sm font-medium">{training.center.address}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Decorative elements */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Training Details */}
          <div className="lg:col-span-2 space-y-4">
            {/* Training Info */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
              <p className="text-gray-600 mb-4">{training.description}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-brand-primary" />
                  <div>
                    <p className="text-sm text-gray-500">{t('trainee.trainingSubscription.trainingInfo.center')}</p>
                    <p className="font-medium">{training.center.name}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-brand-primary" />
                  <div>
                    <p className="text-sm text-gray-500">{t('trainee.trainingSubscription.trainingInfo.address')}</p>
                    <p className="font-medium">{training.center.address}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <BookOpen className="h-5 w-5 text-brand-primary" />
                  <div>
                    <p className="text-sm text-gray-500">{t('trainee.trainingSubscription.trainingInfo.domain')}</p>
                    <p className="font-medium">{training.field}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-brand-primary" />
                  <div>
                    <p className="text-sm text-gray-500">{t('trainee.trainingSubscription.trainingInfo.duration')}</p>
                    <p className="font-medium">{selectedSession?.duration || t('common.notSpecified')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Data Summary - Compact Design */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <User className="h-5 w-5 text-brand-primary" />
                  {t('trainee.trainingSubscription.personalInfo.title')}
                </h3>
                {!isEditingInfo && (formData.current_position || formData.company_name || formData.department || formData.years_of_experience || formData.has_special_accommodation) && (
                  <button
                    onClick={() => setIsEditingInfo(true)}
                    className="text-sm text-brand-primary hover:text-brand-primary/80 font-medium flex items-center gap-1"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    {t('trainee.trainingSubscription.personalInfo.edit')}
                  </button>
                )}
              </div>
              
              {(formData.current_position || formData.company_name || formData.department || formData.years_of_experience || formData.has_special_accommodation) ? (
                <div className="space-y-3">
                  {isEditingInfo ? (
                    // Edit Mode - Compact Form
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('trainee.trainingSubscription.personalInfo.fields.currentPosition')}</label>
                          <input
                            type="text"
                            value={formData.current_position}
                            onChange={(e) => setFormData(prev => ({ ...prev, current_position: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                            placeholder={t('trainee.trainingSubscription.personalInfo.fields.currentPositionPlaceholder')}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('trainee.trainingSubscription.personalInfo.fields.company')}</label>
                          <input
                            type="text"
                            value={formData.company_name}
                            onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                            placeholder={t('trainee.trainingSubscription.personalInfo.fields.companyPlaceholder')}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('trainee.trainingSubscription.personalInfo.fields.department')}</label>
                          <input
                            type="text"
                            value={formData.department}
                            onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                            placeholder={t('trainee.trainingSubscription.personalInfo.fields.departmentPlaceholder')}
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('trainee.trainingSubscription.personalInfo.fields.experience')}</label>
                          <input
                            type="number"
                            min="0"
                            max="50"
                            value={formData.years_of_experience}
                            onChange={(e) => setFormData(prev => ({ ...prev, years_of_experience: e.target.value }))}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                            placeholder={t('trainee.trainingSubscription.personalInfo.fields.experiencePlaceholder')}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-2">{t('trainee.trainingSubscription.personalInfo.fields.specialAccommodation')}</label>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="has_special_accommodation"
                              checked={formData.has_special_accommodation === true}
                              onChange={() => setFormData(prev => ({ ...prev, has_special_accommodation: true }))}
                              className="w-3 h-3 text-brand-primary focus:ring-brand-primary"
                            />
                            <span className="text-sm text-gray-700">{t('trainee.trainingSubscription.personalInfo.fields.yes')}</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="has_special_accommodation"
                              checked={formData.has_special_accommodation === false}
                              onChange={() => setFormData(prev => ({ ...prev, has_special_accommodation: false }))}
                              className="w-3 h-3 text-brand-primary focus:ring-brand-primary"
                            />
                            <span className="text-sm text-gray-700">{t('trainee.trainingSubscription.personalInfo.fields.no')}</span>
                          </label>
                        </div>
                      </div>

                      {formData.has_special_accommodation && (
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">{t('trainee.trainingSubscription.personalInfo.fields.specialAccommodationDetails')}</label>
                          <textarea
                            value={formData.special_accommodation_details}
                            onChange={(e) => setFormData(prev => ({ ...prev, special_accommodation_details: e.target.value }))}
                            rows={2}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                            placeholder={t('trainee.trainingSubscription.personalInfo.fields.specialAccommodationDetailsPlaceholder')}
                          />
                        </div>
                      )}

                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={handleSaveInfo}
                          className="px-4 py-2 bg-brand-primary text-white text-sm rounded-lg hover:bg-brand-primary/90 transition-colors"
                        >
                          {t('trainee.trainingSubscription.personalInfo.actions.save')}
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          {t('trainee.trainingSubscription.personalInfo.actions.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View Mode - Compact Display
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      {formData.current_position && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">{t('trainee.trainingSubscription.personalInfo.fields.currentPosition')}</p>
                          <p className="text-sm font-medium text-gray-900">{formData.current_position}</p>
                        </div>
                      )}
                      {formData.company_name && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">{t('trainee.trainingSubscription.personalInfo.fields.company')}</p>
                          <p className="text-sm font-medium text-gray-900">{formData.company_name}</p>
                        </div>
                      )}
                      {formData.department && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">{t('trainee.trainingSubscription.personalInfo.fields.department')}</p>
                          <p className="text-sm font-medium text-gray-900">{formData.department}</p>
                        </div>
                      )}
                      {formData.years_of_experience && (
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-xs text-gray-500 mb-1">{t('trainee.trainingSubscription.personalInfo.fields.experience')}</p>
                          <p className="text-sm font-medium text-gray-900">{formData.years_of_experience} {t('common.years')}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {formData.has_special_accommodation && !isEditingInfo && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2 text-blue-700">
                        <CheckCircle className="h-4 w-4" />
                        <span className="text-sm font-medium">{t('trainee.trainingSubscription.personalInfo.specialAccommodationRequested')}</span>
                      </div>
                      {formData.special_accommodation_details && (
                        <p className="text-blue-600 text-xs mt-1">{formData.special_accommodation_details}</p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                // Empty State - Compact
                <div className="text-center py-6">
                  <User className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm text-gray-600 mb-3">{t('trainee.trainingSubscription.personalInfo.emptyState.title')}</p>
                  <button
                    onClick={() => setIsEditingInfo(true)}
                    className="bg-gradient-to-r from-brand-primary to-purple-600 text-white px-4 py-2 rounded-lg hover:from-purple-600 hover:to-brand-primary transition-all duration-200 text-sm font-medium"
                  >
                    {t('trainee.trainingSubscription.personalInfo.actions.addInfo')}
                  </button>
                </div>
              )}
            </div>

            {/* Available Sessions */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <Calendar className="h-6 w-6 text-brand-primary" />
                  {t('trainee.trainingSubscription.sessions.title')}
                </h3>
                <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                  {training.sessions.filter(s => !isSessionInPast(s.sessionDate)).length} {t('common.session')}(s)
                </span>
              </div>
              
              {training.sessions.length === 0 ? (
                <div className="text-center py-6">
                  <AlertCircle className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">{t('trainee.trainingSubscription.sessions.noSessions')}</p>
                </div>
              ) : training.sessions.every(s => isSessionInPast(s.sessionDate)) ? (
                <div className="text-center py-6">
                  <AlertCircle className="h-10 w-10 text-orange-400 mx-auto mb-3" />
                  <p className="text-orange-600 font-medium">{t('trainee.trainingSubscription.sessions.allPast')}</p>
                  <p className="text-gray-600 mt-2">{t('trainee.trainingSubscription.sessions.contactCenter')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {training.sessions.map((session) => (
                    <motion.div
                      key={session.id}
                      className={`border-2 rounded-xl p-5 transition-all ${
                        isSessionInPast(session.sessionDate)
                          ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                          : selectedSession?.id === session.id
                          ? 'border-brand-primary bg-brand-primary/5 cursor-pointer shadow-lg'
                          : 'border-gray-200 hover:border-brand-primary/50 cursor-pointer hover:shadow-md'
                      }`}
                      onClick={() => !isSessionInPast(session.sessionDate) && setSelectedSession(session)}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${isSessionInPast(session.sessionDate) ? 'bg-gray-100' : 'bg-brand-primary/10'}`}>
                            <Calendar className={`h-5 w-5 ${isSessionInPast(session.sessionDate) ? 'text-gray-400' : 'text-brand-primary'}`} />
                          </div>
                          <div>
                            <span className={`font-semibold text-lg ${isSessionInPast(session.sessionDate) ? 'text-gray-500' : 'text-gray-900'}`}>
                              {formatDate(session.sessionDate)}
                            </span>
                            {isSessionInPast(session.sessionDate) && (
                              <span className="text-sm text-red-500 ml-2">({t('trainee.trainingSubscription.sessions.status.past')})</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedSession?.id === session.id && (
                            <div className="flex items-center gap-2 text-brand-primary">
                              <CheckCircle className="h-5 w-5" />
                              <span className="text-sm font-medium">{t('trainee.trainingSubscription.sessions.status.selected')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">{t('trainee.trainingSubscription.sessions.details.format')}</p>
                          <p className="font-medium">{getFormatTranslation(session.format)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">{t('trainee.trainingSubscription.sessions.details.price')}</p>
                          <p className="font-medium">{formatCurrency(session.price)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">{t('trainee.trainingSubscription.sessions.details.spots')}</p>
                          <p className="font-medium">{session.availableSpots} {t('common.available')}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">{t('trainee.trainingSubscription.sessions.details.duration')}</p>
                          <p className="font-medium">{getDurationTranslation(session.duration)}</p>
                        </div>
                      </div>
                      
                      {session.isSubscribed && (
                        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-xl">
                          <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle className="h-4 w-4" />
                            <span className="text-sm font-medium">{t('trainee.trainingSubscription.sessions.status.alreadySubscribed')}</span>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Subscription Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-lg p-6 sticky top-8">
              <h3 className="text-lg font-bold text-gray-900 mb-4">{t('trainee.trainingSubscription.summary.title')}</h3>
              
              {selectedSession ? (
                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-2xl p-4">
                    <h4 className="font-semibold text-gray-900 mb-3">{t('trainee.trainingSubscription.summary.selectedSession')}</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('common.date')}:</span>
                        <span className="font-medium">{formatDate(selectedSession.sessionDate)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('trainee.trainingSubscription.sessions.details.format')}:</span>
                        <span className="font-medium">{getFormatTranslation(selectedSession.format)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('trainee.trainingSubscription.sessions.details.duration')}:</span>
                        <span className="font-medium">{getDurationTranslation(selectedSession.duration)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('trainee.trainingSubscription.sessions.details.spots')}:</span>
                        <span className="font-medium">{selectedSession.availableSpots} {t('common.available')}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="border-t pt-6">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-lg font-semibold text-gray-900">{t('trainee.trainingSubscription.summary.total')}</span>
                      <span className="text-2xl font-bold text-brand-primary">
                        {formatCurrency(selectedSession.price)}
                      </span>
                    </div>
                    
                    {error && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                        <div className="flex items-center gap-2 text-red-700">
                          <X className="h-4 w-4" />
                          <span className="text-sm">{error}</span>
                        </div>
                      </div>
                    )}
                    
                    <button
                      onClick={handleSubscribe}
                      disabled={subscribing || selectedSession.isSubscribed || selectedSession.availableSpots === 0 || isSessionInPast(selectedSession.sessionDate)}
                      className={`w-full py-4 px-6 rounded-2xl font-semibold transition-all ${
                        selectedSession.isSubscribed
                          ? 'bg-green-100 text-green-700 cursor-not-allowed'
                          : selectedSession.availableSpots === 0
                          ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                          : isSessionInPast(selectedSession.sessionDate)
                          ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-brand-primary to-brand-primary/80 text-white hover:from-brand-primary/90 hover:to-brand-primary/70 shadow-lg hover:shadow-xl transform hover:-translate-y-1'
                      }`}
                    >
                      {subscribing ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          {t('trainee.trainingSubscription.summary.actions.registering')}
                        </div>
                      ) : selectedSession.isSubscribed ? (
                        <div className="flex items-center justify-center gap-2">
                          <CheckCircle className="h-4 w-4" />
                          {t('trainee.trainingSubscription.summary.actions.alreadyEnrolled')}
                        </div>
                      ) : selectedSession.availableSpots === 0 ? (
                        t('trainee.trainingSubscription.summary.actions.sessionFull')
                      ) : isSessionInPast(selectedSession.sessionDate) ? (
                        t('trainee.trainingSubscription.summary.actions.sessionPast')
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          {t('trainee.trainingSubscription.summary.actions.confirmRegistration')}
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">{t('trainee.trainingSubscription.summary.selectSession')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Existing Subscription Dialog */}
      {showChangeDialog && existingSubscription && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              <AlertCircle className="h-16 w-16 text-amber-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {t('trainee.trainingSubscription.changeSession.title')}
              </h3>
              <p className="text-gray-600 mb-6">
                {t('trainee.trainingSubscription.changeSession.message')}
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="font-medium text-gray-900">
                    {formatDate(existingSubscription.sessionDate)}
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  {existingSubscription.trainingTitle}
                </p>
              </div>

              <p className="text-gray-600 mb-6">
                {t('trainee.trainingSubscription.changeSession.changeToSelected')}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowChangeDialog(false)}
                  className="flex-1 py-3 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleChangeSession}
                  disabled={subscribing}
                  className="flex-1 py-3 px-4 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors disabled:opacity-50"
                >
                  {subscribing ? t('trainee.trainingSubscription.changeSession.changing') : t('trainee.trainingSubscription.changeSession.changeSession')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default TrainingSubscriptionPage;
